import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateNote } from './useCreateNote'
import { useSaveNote } from './useSaveNote'
import { useNotification } from '../providers/NotificationProvider'
import {
  filenameToDate,
  filenameToKey,
  NoteType,
  type Note,
} from '../utils/note'
import { useAuthenticatedUser } from '../providers/UserProvider'
import { useCreateImportLogNote } from '../utils/import/createImportLogNote'
import {
  createProcessingNotification,
  createCompletionNotification,
  createSignificantErrorNotification,
  type ImportConflictDialogProps,
  type ImportConflictChoice,
} from '../utils/import/core'
import { runImport, type EngineOptions } from '../utils/import/engine'
import { MAX_ATTACHMENT_SIZE_BYTES } from '../utils/import/config'
import { createConflictResolver } from '../utils/import/conflictResolver'
import { cachedNotesContext, getCachedData } from '../contexts/CachedNotes'
import { cacheKeyFromNoteType } from '../utils/queryKeyFactory'
import { createImportedNotesFolder } from '../utils/import/folderHelpers'

/**
 * Simplified hook for importing markdown files into NotePlan
 *
 * Features:
 * - Binary content detection and filtering (prevents importing non-text files)
 * - Calendar auto-detection from filename patterns (2024-11-01.md → calendar note)
 * - Conflict resolution with session-scoped "Don't ask again" toggle
 * - Lazy evaluation - only runs conflict detection when files are imported
 * - Pure function-based conflict detection for better performance
 * - Comprehensive error tracking and user-friendly notifications
 *
 * Performance improvements:
 * - No eager computation on every render
 * - Direct conflict detection using pure functions
 * - Minimal memory allocation during import process
 */

export function useImportNotes() {
  const user = useAuthenticatedUser()
  const createNote = useCreateNote()
  const saveNote = useSaveNote()
  const { showNotification } = useNotification()
  const createImportLogNote = useCreateImportLogNote()
  const cachedNotesQueryClient = useQueryClient({ context: cachedNotesContext })

  const [filesAttempted, setFilesAttempted] = useState<string[]>([])

  const importFiles = useCallback(
    async (
      files: FileList,
      _targetFolder: string,
      showConflictDialog: (
        properties: ImportConflictDialogProps
      ) => Promise<{ choice: ImportConflictChoice; remember: boolean }>
    ) => {
      setFilesAttempted([...files].map((file) => file.name))

      // Show processing notification
      showNotification(createProcessingNotification(files.length, 'file'))

      // Convert FileList to ImportItem[] format for the unified engine
      const items = [...files].map((file) => ({
        kind: 'note' as const,
        note: {
          path: file.name, // For single file imports, path is just the filename
          file,
          isCalendar: Boolean(filenameToDate(file.name).date),
          attachments: [], // Single file imports don't have attachments
        },
      }))

      // Create conflict resolver
      const conflictResolver = createConflictResolver(showConflictDialog)

      // Create wrapper functions for the mutations
      const createNoteFunction = async (parameters: {
        recordName: string
        noteType: NoteType
        filename: string
        content: string
        parent: string | null
        isDir?: boolean
      }): Promise<Note> => {
        return createNote.mutateAsync(parameters)
      }

      const saveNoteFunction = async (parameters: {
        content: string
        attachments?: string[]
        recordName: string
        filename: string
        parent: string | null
        noteType: NoteType
        modificationDate: Date
      }): Promise<Note> => {
        return saveNote.mutateAsync(parameters)
      }

      try {
        // Live cache getters
        const getProjectNotesMap = (): Map<string, Note> => {
          const projectKey = cacheKeyFromNoteType(NoteType.PROJECT_NOTE, user)
          return getCachedData(cachedNotesQueryClient, projectKey)
        }

        // Create "Imported Notes" folder with auto-incrementing suffix
        const { folderName, folder } = await createImportedNotesFolder(
          createNoteFunction,
          getProjectNotesMap()
        )

        // Map the folder for the engine
        const folderIdMap = new Map<string, string>()
        folderIdMap.set(folderName, folder.recordName ?? '')

        const getCalendarNotesByDate = (): Map<string, Note[]> => {
          const calendarKey = cacheKeyFromNoteType(NoteType.CALENDAR_NOTE, user)
          const calendarMap = getCachedData(cachedNotesQueryClient, calendarKey)
          const grouped = new Map<string, Note[]>()
          for (const note of calendarMap.values()) {
            const key = filenameToKey(note.filename)
            if (!key) continue
            const array = grouped.get(key) ?? []
            array.push(note)
            grouped.set(key, array)
          }
          return grouped
        }

        // Prepare engine options
        const engineOptions: EngineOptions = {
          attachmentStrategy: undefined, // No attachments for single file imports
          onProgress: (done, total) => {
            const progress = Math.round((done / total) * 100)
            showNotification({
              title: `Importing ${items.length === 1 ? 'file' : 'files'}…`,
              message: `${String(progress)}% completed (${String(done)}/${String(total)} files)`,
              autoHide: false,
            })
          },
          onConflict: conflictResolver.resolve,
          maxAttachmentBytes: MAX_ATTACHMENT_SIZE_BYTES, // 50MB default
          authType: user.authType,
          getProjectNotesMap,
          getCalendarNotesByDate,
          folderIdMap,
          targetRootFolder: folderName,
          createNote: createNoteFunction,
          saveNote: saveNoteFunction,
        }

        // Call the unified import engine
        const result = await runImport(items, engineOptions)

        // Show completion notification with attachment failure count
        showNotification(createCompletionNotification(result.stats, result.attachmentsFailed.length))

        // Create import log with enhanced logging system
        createImportLogNote({
          filesAttempted,
          stats: result.stats,
          errors: result.errors,
          warnings: result.warnings,
          hints: result.hints,
          attachmentsFailed: result.attachmentsFailed,
        })

        // Show additional error notification if there were significant errors
        const significantErrorNotification = createSignificantErrorNotification(
          result.stats.errors,
          files.length
        )
        if (significantErrorNotification) {
          showNotification(significantErrorNotification)
        }
      } catch (error) {
        console.error('useImportMarkdown: Import failed', error)
        showNotification({
          title: 'Import Error',
          message: 'Failed to import files. Please try again.',
          autoHide: false,
        })
      }
    },
    [
      user,
      createNote,
      saveNote,
      showNotification,
      createImportLogNote,
      filesAttempted,
      cachedNotesQueryClient,
    ]
  )

  return { importFiles }
}
